/**
 * Note mention labels (`[[id|label]]`) are capped so linking a note with a very
 * long title does not insert a giant chip. Applied only where a label is created
 * from a title (the `[[` picker), never to mentions already in a note or typed
 * by hand. Mirrored in Swift (CaliuCore) and Kotlin (`:core`), all three running
 * `mentionLabel.fixtures.json`. Change all three or none.
 */

/** Maximum label length, in user-perceived characters (grapheme clusters). */
export const MENTION_LABEL_MAX_LENGTH = 60

const ELLIPSIS = "…"

// `Intl.Segmenter` is ES2022; typed locally so ES2021-lib consumers compile.
type GraphemeSegmenter = { segment(input: string): Iterable<{ segment: string }> }
type SegmenterConstructor = new (locale?: string, options?: { granularity: "grapheme" }) => GraphemeSegmenter
const Segmenter = (Intl as unknown as { Segmenter?: SegmenterConstructor }).Segmenter
const graphemeSegmenter = Segmenter ? new Segmenter(undefined, { granularity: "grapheme" }) : null

function graphemes(text: string): string[] {
  // Code points are the fallback where `Intl.Segmenter` is missing.
  if (!graphemeSegmenter) return Array.from(text)
  return Array.from(graphemeSegmenter.segment(text), (s) => s.segment)
}

/**
 * Trim `label`; when it is longer than `MENTION_LABEL_MAX_LENGTH` graphemes,
 * keep the first `MENTION_LABEL_MAX_LENGTH - 1`, trim trailing whitespace and
 * append `…`.
 */
export function truncateMentionLabel(label: string): string {
  const trimmed = label.trim()
  const chars = graphemes(trimmed)
  if (chars.length <= MENTION_LABEL_MAX_LENGTH) return trimmed
  return chars.slice(0, MENTION_LABEL_MAX_LENGTH - 1).join("").trimEnd() + ELLIPSIS
}
